import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    Search, FileText, Download, Unlink, Replace, Calculator, 
    MessageSquare, Phone, Edit3, Save, X, Loader2, 
    FileImage, FileCheck, Receipt, AlertTriangle, Package,
    ChevronRight, RefreshCw, Trash2, Link2, DollarSign, Printer
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OfficeDeskPage() {
    const { loadId: urlLoadId } = useParams();
    const navigate = useNavigate();
    
    // State
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [selectedLoad, setSelectedLoad] = useState(null);
    const [loadDocuments, setLoadDocuments] = useState([]);
    const [communications, setCommunications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [truckProfile, setTruckProfile] = useState(null);
    
    // Invoice state
    const [brokerInvoice, setBrokerInvoice] = useState(null);
    const [dispatcherInvoice, setDispatcherInvoice] = useState(null);
    const [loadingInvoices, setLoadingInvoices] = useState(false);
    
    // Edit state
    const [isEditing, setIsEditing] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [saving, setSaving] = useState(false);
    
    // Dialog states
    const [unlinkDialog, setUnlinkDialog] = useState({ open: false, document: null });
    const [replaceDialog, setReplaceDialog] = useState({ open: false, document: null });
    const [replaceFile, setReplaceFile] = useState(null);

    // Fetch load by ID
    const fetchLoad = useCallback(async (loadId) => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/loads/${loadId}`, {
                credentials: 'include'
            });
            if (res.ok) {
                const data = await res.json();
                setSelectedLoad(data);
                setEditForm({
                    rate: data.rate || 0,
                    gross_pay: data.gross_pay || data.rate || 0,
                    distance: data.distance || 0,
                    detention_pay: data.detention_pay || 0,
                    lumper_fee: data.lumper_fee || 0,
                    notes: data.notes || ''
                });
                
                // Fetch truck profile if truck assigned
                if (data.truck_id) {
                    const truckRes = await fetch(`${API_URL}/api/trucks/${data.truck_id}`, {
                        credentials: 'include'
                    });
                    if (truckRes.ok) {
                        const truckData = await truckRes.json();
                        setTruckProfile(truckData);
                    }
                }
                
                // Fetch documents
                fetchDocuments(loadId);
                // Fetch communications
                fetchCommunications(loadId);
                // Fetch invoices
                fetchInvoices(loadId, data);
            } else {
                toast.error('Load not found');
            }
        } catch (error) {
            console.error('Error fetching load:', error);
            toast.error('Failed to fetch load');
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch documents for load
    const fetchDocuments = async (loadId) => {
        try {
            const res = await fetch(`${API_URL}/api/load-documents?load_id=${loadId}`, {
                credentials: 'include'
            });
            if (res.ok) {
                const data = await res.json();
                setLoadDocuments(data);
            }
        } catch (error) {
            console.error('Error fetching documents:', error);
        }
    };

    // Fetch communications (notes + call recordings)
    const fetchCommunications = async (loadId) => {
        try {
            const res = await fetch(`${API_URL}/api/office/communications/${loadId}`, {
                credentials: 'include'
            });
            if (res.ok) {
                const data = await res.json();
                setCommunications(data);
            }
        } catch (error) {
            console.error('Error fetching communications:', error);
            setCommunications([]);
        }
    };

    // Fetch invoices for load
    const fetchInvoices = async (loadId, loadData) => {
        setLoadingInvoices(true);
        try {
            // Fetch broker invoice from invoices collection
            const invoicesRes = await fetch(`${API_URL}/api/invoices`, {
                credentials: 'include'
            });
            if (invoicesRes.ok) {
                const invoices = await invoicesRes.json();
                const brokerInv = invoices.find(inv => inv.load_id === loadId);
                setBrokerInvoice(brokerInv || null);
            }
            
            // Check if dispatcher invoice exists (from load data)
            if (loadData?.dispatcher_invoice_id) {
                const commissionRate = loadData.commission_snapshot || truckProfile?.commission_percent || 10;
                const grossPay = loadData.gross_pay || loadData.rate || 0;
                const dispatcherFee = grossPay * (commissionRate / 100);
                
                setDispatcherInvoice({
                    invoice_id: loadData.dispatcher_invoice_id,
                    load_id: loadId,
                    truck_id: loadData.truck_id,
                    broker_name: loadData.broker_name,
                    origin: `${loadData.origin_city}, ${loadData.origin_state}`,
                    destination: `${loadData.destination_city}, ${loadData.destination_state}`,
                    gross_rate: grossPay,
                    commission_rate: commissionRate,
                    dispatcher_fee: dispatcherFee,
                    status: loadData.dispatcher_invoice_status,
                    invoice_date: loadData.dispatcher_invoice_date,
                    due_date: loadData.dispatcher_invoice_due_date,
                    paid_date: loadData.dispatcher_invoice_paid_date
                });
            } else {
                setDispatcherInvoice(null);
            }
        } catch (error) {
            console.error('Error fetching invoices:', error);
        } finally {
            setLoadingInvoices(false);
        }
    };

    // Generate Broker Invoice PDF
    const generateBrokerInvoicePDF = (invoice) => {
        const doc = new jsPDF();
        const load = selectedLoad;
        
        // Header background
        doc.setFillColor(39, 39, 42); // Zinc-800
        doc.rect(0, 0, 220, 45, 'F');
        
        // Company Logo/Name
        doc.setFontSize(24);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(249, 115, 22); // Orange
        doc.text('LoadTMS', 20, 25);
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(161, 161, 170); // Zinc-400
        doc.text('DISPATCH COMMAND CENTER', 20, 32);
        
        // Invoice Title
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 255, 255);
        doc.text('BROKER INVOICE', 130, 25);
        
        doc.setFontSize(10);
        doc.setTextColor(161, 161, 170);
        doc.text(`#${invoice.invoice_id}`, 130, 32);
        
        // Reset text color for body
        doc.setTextColor(0, 0, 0);
        
        // Invoice Details Section
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Invoice Details', 20, 55);
        
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        
        let yPos = 62;
        const invoiceDetails = [
            ['Invoice ID:', invoice.invoice_id],
            ['Date Issued:', invoice.created_at ? new Date(invoice.created_at).toLocaleDateString() : 'N/A'],
            ['Due Date:', invoice.due_date || 'N/A'],
            ['Status:', (invoice.status || 'pending').toUpperCase()],
        ];
        
        invoiceDetails.forEach(([label, value]) => {
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(100, 100, 100);
            doc.text(label, 20, yPos);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(0, 0, 0);
            doc.text(value || 'N/A', 55, yPos);
            yPos += 6;
        });
        
        // Load Details Section
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Load Information', 110, 55);
        
        yPos = 62;
        const loadDetails = [
            ['Load ID:', load?.load_id || invoice.load_id],
            ['Origin:', load ? `${load.origin_city}, ${load.origin_state}` : 'N/A'],
            ['Destination:', load ? `${load.destination_city}, ${load.destination_state}` : 'N/A'],
            ['Broker:', load?.broker_name || 'N/A'],
        ];
        
        loadDetails.forEach(([label, value]) => {
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(100, 100, 100);
            doc.text(label, 110, yPos);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(0, 0, 0);
            doc.text(value || 'N/A', 145, yPos);
            yPos += 6;
        });
        
        // Charges Table
        autoTable(doc, {
            startY: 100,
            head: [['Description', 'Amount']],
            body: [
                ['Freight Charges', `$${(invoice.amount || load?.gross_pay || 0).toFixed(2)}`],
            ],
            theme: 'striped',
            headStyles: { fillColor: [249, 115, 22] },
            styles: { fontSize: 10 }
        });
        
        // Total
        const finalY = doc.lastAutoTable.finalY + 10;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.text('TOTAL DUE:', 130, finalY);
        doc.setFontSize(16);
        doc.setTextColor(249, 115, 22);
        doc.text(`$${(invoice.amount || load?.gross_pay || 0).toFixed(2)}`, 170, finalY);
        
        // Save
        doc.save(`broker_invoice_${invoice.invoice_id}.pdf`);
        toast.success('Broker invoice PDF downloaded!');
    };

    // Generate Dispatcher (Commission) Invoice PDF
    const generateDispatcherInvoicePDF = (data) => {
        const doc = new jsPDF();
        
        // Header - Big T's Logistics branding
        doc.setFillColor(249, 115, 22); // Orange
        doc.rect(0, 0, 220, 35, 'F');
        
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(24);
        doc.setFont('helvetica', 'bold');
        doc.text("Big T's Logistics", 14, 18);
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text("Dispatch Services", 14, 26);
        
        // Invoice info
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.text(`Invoice: ${data.invoice_id}`, 145, 18);
        doc.setFontSize(10);
        doc.text(`Date: ${data.invoice_date || new Date().toLocaleDateString()}`, 145, 26);
        
        // Reset colors
        doc.setTextColor(0, 0, 0);
        
        // Bill To
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text("BILL TO:", 14, 50);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.text(`Carrier / Truck: ${data.truck_id || 'N/A'}`, 14, 58);
        doc.text(`Broker: ${data.broker_name || 'N/A'}`, 14, 65);
        
        // Load Details
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text("LOAD DETAILS:", 14, 82);
        
        autoTable(doc, {
            startY: 88,
            head: [['Description', 'Route', 'Gross Rate', 'Commission %', 'Dispatch Fee']],
            body: [
                [
                    `Load ${data.load_id}`,
                    `${data.origin || 'N/A'} → ${data.destination || 'N/A'}`,
                    `$${(data.gross_rate || 0).toFixed(2)}`,
                    `${data.commission_rate || 10}%`,
                    `$${(data.dispatcher_fee || 0).toFixed(2)}`
                ]
            ],
            theme: 'striped',
            headStyles: { fillColor: [249, 115, 22] },
            styles: { fontSize: 9 }
        });
        
        // Total
        const finalY = doc.lastAutoTable.finalY + 10;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.text("TOTAL DUE:", 120, finalY);
        doc.setTextColor(249, 115, 22);
        doc.setFontSize(16);
        doc.text(`$${(data.dispatcher_fee || 0).toFixed(2)}`, 165, finalY);
        
        // Payment Terms
        doc.setTextColor(100, 100, 100);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text("Payment Terms: Net 7 days", 14, finalY + 15);
        doc.text(`Due Date: ${data.due_date || 'N/A'}`, 14, finalY + 22);
        
        // Footer
        doc.setFontSize(8);
        doc.text("Thank you for your business!", 14, 280);
        
        // Save
        doc.save(`dispatcher_invoice_${data.invoice_id}.pdf`);
        toast.success('Dispatcher invoice PDF downloaded!');
    };

    // Search loads
    const handleSearch = async () => {
        if (!searchQuery.trim()) return;
        setSearching(true);
        try {
            const res = await fetch(`${API_URL}/api/loads?search=${encodeURIComponent(searchQuery)}`, {
                credentials: 'include'
            });
            if (res.ok) {
                const data = await res.json();
                setSearchResults(data.slice(0, 10)); // Limit to 10 results
            }
        } catch (error) {
            console.error('Search error:', error);
            toast.error('Search failed');
        } finally {
            setSearching(false);
        }
    };

    // Select load from search
    const selectLoad = (load) => {
        navigate(`/office-desk/${load.load_id}`);
        setSearchResults([]);
        setSearchQuery('');
    };

    // Load from URL param
    useEffect(() => {
        if (urlLoadId) {
            fetchLoad(urlLoadId);
        }
    }, [urlLoadId, fetchLoad]);

    // Save load edits
    const handleSaveEdits = async () => {
        setSaving(true);
        try {
            const res = await fetch(`${API_URL}/api/office/loads/${selectedLoad.load_id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(editForm)
            });
            
            if (res.ok) {
                const updated = await res.json();
                setSelectedLoad(updated);
                setIsEditing(false);
                toast.success('Load updated successfully');
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Failed to update load');
            }
        } catch (error) {
            console.error('Save error:', error);
            toast.error('Failed to save changes');
        } finally {
            setSaving(false);
        }
    };

    // Unlink document
    const handleUnlinkDocument = async () => {
        const doc = unlinkDialog.document;
        if (!doc) return;
        
        try {
            const res = await fetch(`${API_URL}/api/office/documents/${doc.document_id}/unlink`, {
                method: 'POST',
                credentials: 'include'
            });
            
            if (res.ok) {
                toast.success('Document unlinked');
                fetchDocuments(selectedLoad.load_id);
            } else {
                toast.error('Failed to unlink document');
            }
        } catch (error) {
            console.error('Unlink error:', error);
            toast.error('Failed to unlink document');
        } finally {
            setUnlinkDialog({ open: false, document: null });
        }
    };

    // Replace document
    const handleReplaceDocument = async () => {
        const doc = replaceDialog.document;
        if (!doc || !replaceFile) return;
        
        const formData = new FormData();
        formData.append('file', replaceFile);
        formData.append('document_type', doc.document_type);
        formData.append('load_id', selectedLoad.load_id);
        
        try {
            // First delete old document
            await fetch(`${API_URL}/api/load-documents/${doc.document_id}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            
            // Upload new document
            const res = await fetch(`${API_URL}/api/documents/upload`, {
                method: 'POST',
                credentials: 'include',
                body: formData
            });
            
            if (res.ok) {
                toast.success('Document replaced successfully');
                fetchDocuments(selectedLoad.load_id);
            } else {
                toast.error('Failed to upload replacement');
            }
        } catch (error) {
            console.error('Replace error:', error);
            toast.error('Failed to replace document');
        } finally {
            setReplaceDialog({ open: false, document: null });
            setReplaceFile(null);
        }
    };

    // Download dispute bundle (signed rate con + POD)
    const handleDownloadDisputeBundle = async () => {
        try {
            const res = await fetch(`${API_URL}/api/office/dispute-bundle/${selectedLoad.load_id}`, {
                credentials: 'include'
            });
            
            if (res.ok) {
                const blob = await res.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `dispute_bundle_${selectedLoad.load_id}.zip`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                a.remove();
                toast.success('Dispute bundle downloaded');
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Failed to create dispute bundle');
            }
        } catch (error) {
            console.error('Bundle error:', error);
            toast.error('Failed to download dispute bundle');
        }
    };

    // Calculate financials
    const calculateFinancials = () => {
        const gross = parseFloat(editForm.gross_pay) || parseFloat(editForm.rate) || 0;
        const commissionRate = selectedLoad?.commission_snapshot || truckProfile?.commission_percent || 10;
        const commission = gross * (commissionRate / 100);
        const detention = parseFloat(editForm.detention_pay) || 0;
        const lumper = parseFloat(editForm.lumper_fee) || 0;
        const netToDriver = gross - commission + detention - lumper;
        
        return {
            gross,
            commissionRate,
            commission,
            detention,
            lumper,
            netToDriver,
            taxReserve: commission * 0.25
        };
    };

    const financials = calculateFinancials();

    // Document type icons
    const getDocIcon = (type) => {
        switch (type?.toLowerCase()) {
            case 'ratecon': return <FileCheck className="w-4 h-4 text-orange-400" />;
            case 'bol': return <FileText className="w-4 h-4 text-blue-400" />;
            case 'pod': return <FileImage className="w-4 h-4 text-green-400" />;
            case 'invoice': return <Receipt className="w-4 h-4 text-purple-400" />;
            default: return <FileText className="w-4 h-4 text-zinc-400" />;
        }
    };

    return (
        <div className="p-6 space-y-6" data-testid="office-desk-page">
            {/* Header with Search */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white tracking-tight uppercase">Office Desk</h1>
                    <p className="text-zinc-400 text-sm">Audit, Edit & Investigate Loads</p>
                </div>
                
                {/* Search Bar */}
                <div className="relative w-full md:w-96">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                        placeholder="Search by Load ID (e.g., LD-SIM-XXXX)"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        className="pl-10 bg-zinc-800 border-zinc-700 text-white"
                        data-testid="load-search-input"
                    />
                    {searching && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-zinc-400" />
                    )}
                    
                    {/* Search Results Dropdown */}
                    {searchResults.length > 0 && (
                        <div className="absolute top-full mt-1 w-full bg-zinc-800 border border-zinc-700 rounded-md shadow-lg z-50 max-h-64 overflow-auto">
                            {searchResults.map((load) => (
                                <button
                                    key={load.load_id}
                                    onClick={() => selectLoad(load)}
                                    className="w-full px-4 py-2 text-left hover:bg-zinc-700 flex items-center justify-between"
                                    data-testid={`search-result-${load.load_id}`}
                                >
                                    <span className="text-white font-mono">{load.load_id}</span>
                                    <span className="text-zinc-400 text-sm">
                                        {load.origin_city} → {load.destination_city}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* No Load Selected */}
            {!selectedLoad && !loading && (
                <Card className="bg-zinc-800/50 border-zinc-700">
                    <CardContent className="py-12 text-center">
                        <Package className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                        <p className="text-zinc-400">Search for a load ID to begin auditing</p>
                        <p className="text-zinc-500 text-sm mt-2">
                            Or click the edit icon on any load to open it here
                        </p>
                    </CardContent>
                </Card>
            )}

            {/* Loading */}
            {loading && (
                <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                </div>
            )}

            {/* Load Content */}
            {selectedLoad && !loading && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Content - 2 columns */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Load Header */}
                        <Card className="bg-zinc-800/50 border-zinc-700">
                            <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle className="text-xl text-white font-mono">
                                            {selectedLoad.load_id}
                                        </CardTitle>
                                        <p className="text-zinc-400 text-sm mt-1">
                                            {selectedLoad.origin_city}, {selectedLoad.origin_state} → {selectedLoad.destination_city}, {selectedLoad.destination_state}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Badge variant="outline" className={
                                            selectedLoad.status === 'delivered' ? 'border-green-500 text-green-400' :
                                            selectedLoad.status === 'in_transit' ? 'border-blue-500 text-blue-400' :
                                            'border-zinc-500 text-zinc-400'
                                        }>
                                            {selectedLoad.status?.toUpperCase()}
                                        </Badge>
                                        {!isEditing ? (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="border-zinc-700"
                                                onClick={() => setIsEditing(true)}
                                                data-testid="edit-load-btn"
                                            >
                                                <Edit3 className="w-4 h-4 mr-1" />
                                                Edit
                                            </Button>
                                        ) : (
                                            <div className="flex gap-2">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="border-zinc-700"
                                                    onClick={() => setIsEditing(false)}
                                                >
                                                    <X className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    className="bg-green-600 hover:bg-green-700"
                                                    onClick={handleSaveEdits}
                                                    disabled={saving}
                                                    data-testid="save-edits-btn"
                                                >
                                                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                                                    Save
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {/* Editable Fields */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div>
                                        <label className="text-zinc-500 text-xs">Rate</label>
                                        {isEditing ? (
                                            <Input
                                                type="number"
                                                value={editForm.rate}
                                                onChange={(e) => setEditForm({...editForm, rate: e.target.value})}
                                                className="bg-zinc-900 border-zinc-600 text-white mt-1"
                                            />
                                        ) : (
                                            <p className="text-white font-semibold">${selectedLoad.rate?.toLocaleString()}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="text-zinc-500 text-xs">Gross Pay</label>
                                        {isEditing ? (
                                            <Input
                                                type="number"
                                                value={editForm.gross_pay}
                                                onChange={(e) => setEditForm({...editForm, gross_pay: e.target.value})}
                                                className="bg-zinc-900 border-zinc-600 text-white mt-1"
                                            />
                                        ) : (
                                            <p className="text-green-400 font-semibold">${selectedLoad.gross_pay?.toLocaleString() || selectedLoad.rate?.toLocaleString()}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="text-zinc-500 text-xs">Distance (mi)</label>
                                        {isEditing ? (
                                            <Input
                                                type="number"
                                                value={editForm.distance}
                                                onChange={(e) => setEditForm({...editForm, distance: e.target.value})}
                                                className="bg-zinc-900 border-zinc-600 text-white mt-1"
                                            />
                                        ) : (
                                            <p className="text-white">{selectedLoad.distance || '-'}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="text-zinc-500 text-xs">Detention Pay</label>
                                        {isEditing ? (
                                            <Input
                                                type="number"
                                                value={editForm.detention_pay}
                                                onChange={(e) => setEditForm({...editForm, detention_pay: e.target.value})}
                                                className="bg-zinc-900 border-zinc-600 text-white mt-1"
                                            />
                                        ) : (
                                            <p className="text-amber-400">${selectedLoad.detention_pay || 0}</p>
                                        )}
                                    </div>
                                </div>
                                
                                {/* Notes */}
                                {isEditing && (
                                    <div className="mt-4">
                                        <label className="text-zinc-500 text-xs">Notes</label>
                                        <Input
                                            value={editForm.notes}
                                            onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                                            className="bg-zinc-900 border-zinc-600 text-white mt-1"
                                            placeholder="Add notes..."
                                        />
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Tabs for Documents, Communications, and Invoices */}
                        <Tabs defaultValue="documents" className="w-full">
                            <TabsList className="bg-zinc-800 border-zinc-700">
                                <TabsTrigger value="documents" className="data-[state=active]:bg-zinc-700">
                                    <FileText className="w-4 h-4 mr-2" />
                                    File Explorer
                                </TabsTrigger>
                                <TabsTrigger value="communications" className="data-[state=active]:bg-zinc-700">
                                    <MessageSquare className="w-4 h-4 mr-2" />
                                    Communication Vault
                                </TabsTrigger>
                                <TabsTrigger value="invoices" className="data-[state=active]:bg-zinc-700" data-testid="invoices-tab">
                                    <Receipt className="w-4 h-4 mr-2" />
                                    Invoices
                                </TabsTrigger>
                            </TabsList>

                            {/* Documents Tab */}
                            <TabsContent value="documents" className="mt-4">
                                <Card className="bg-zinc-800/50 border-zinc-700">
                                    <CardHeader className="pb-3 flex flex-row items-center justify-between">
                                        <CardTitle className="text-base text-white">All Load Documents</CardTitle>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="border-zinc-700"
                                            onClick={() => fetchDocuments(selectedLoad.load_id)}
                                        >
                                            <RefreshCw className="w-4 h-4" />
                                        </Button>
                                    </CardHeader>
                                    <CardContent>
                                        {loadDocuments.length === 0 ? (
                                            <p className="text-zinc-500 text-sm text-center py-4">No documents attached to this load</p>
                                        ) : (
                                            <div className="space-y-2">
                                                {loadDocuments.map((doc) => (
                                                    <div key={doc.document_id} className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-lg">
                                                        <div className="flex items-center gap-3">
                                                            {getDocIcon(doc.document_type)}
                                                            <div>
                                                                <p className="text-white text-sm font-medium">
                                                                    {doc.filename || doc.original_filename || doc.document_type?.toUpperCase()}
                                                                </p>
                                                                <p className="text-zinc-500 text-xs">
                                                                    {doc.document_type?.toUpperCase()} • {doc.signed ? 'Signed' : 'Unsigned'}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            {/* View */}
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                className="text-zinc-400 hover:text-white"
                                                                onClick={() => {
                                                                    let filePath = doc.signed_file_path || doc.file_path;
                                                                    if (filePath?.startsWith('uploads/')) {
                                                                        filePath = filePath.substring(8);
                                                                    }
                                                                    window.open(`${API_URL}/api/uploads/${filePath}`, '_blank');
                                                                }}
                                                                data-testid={`view-doc-${doc.document_id}`}
                                                            >
                                                                <FileText className="w-4 h-4" />
                                                            </Button>
                                                            {/* Unlink */}
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                className="text-amber-400 hover:text-amber-300"
                                                                onClick={() => setUnlinkDialog({ open: true, document: doc })}
                                                                data-testid={`unlink-doc-${doc.document_id}`}
                                                            >
                                                                <Unlink className="w-4 h-4" />
                                                            </Button>
                                                            {/* Replace */}
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                className="text-blue-400 hover:text-blue-300"
                                                                onClick={() => setReplaceDialog({ open: true, document: doc })}
                                                                data-testid={`replace-doc-${doc.document_id}`}
                                                            >
                                                                <Replace className="w-4 h-4" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            {/* Communications Tab */}
                            <TabsContent value="communications" className="mt-4">
                                <Card className="bg-zinc-800/50 border-zinc-700">
                                    <CardHeader className="pb-3">
                                        <CardTitle className="text-base text-white">Notes & Call Transcripts</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        {communications.length === 0 ? (
                                            <p className="text-zinc-500 text-sm text-center py-4">No communications recorded for this load</p>
                                        ) : (
                                            <div className="space-y-3 max-h-96 overflow-auto">
                                                {communications.map((comm, idx) => (
                                                    <div key={idx} className="p-3 bg-zinc-900/50 rounded-lg">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            {comm.type === 'call' ? (
                                                                <Phone className="w-4 h-4 text-green-400" />
                                                            ) : (
                                                                <MessageSquare className="w-4 h-4 text-blue-400" />
                                                            )}
                                                            <span className="text-zinc-400 text-xs">
                                                                {comm.type === 'call' ? 'Call Recording' : 'Note'} • {new Date(comm.created_at).toLocaleString()}
                                                            </span>
                                                        </div>
                                                        <p className="text-white text-sm">{comm.content || comm.transcript}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            {/* Invoices Tab */}
                            <TabsContent value="invoices" className="mt-4" data-testid="invoices-tab-content">
                                <Card className="bg-zinc-800/50 border-zinc-700">
                                    <CardHeader className="pb-3">
                                        <CardTitle className="text-base text-white flex items-center gap-2">
                                            <Receipt className="w-4 h-4 text-purple-400" />
                                            Load Invoices
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        {loadingInvoices ? (
                                            <div className="flex justify-center py-8">
                                                <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
                                            </div>
                                        ) : (
                                            <>
                                                {/* Broker Invoice */}
                                                <div className="p-4 bg-zinc-900/50 rounded-lg border border-zinc-700" data-testid="broker-invoice-section">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <div className="flex items-center gap-2">
                                                            <DollarSign className="w-5 h-5 text-green-400" />
                                                            <h4 className="text-white font-medium">Broker Invoice</h4>
                                                        </div>
                                                        {brokerInvoice && (
                                                            <Badge className={`${
                                                                brokerInvoice.status === 'paid' ? 'bg-green-500/20 text-green-400' :
                                                                brokerInvoice.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                                                                'bg-zinc-500/20 text-zinc-400'
                                                            }`}>
                                                                {(brokerInvoice.status || 'pending').toUpperCase()}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    
                                                    {brokerInvoice ? (
                                                        <div className="space-y-2">
                                                            <div className="grid grid-cols-2 gap-2 text-sm">
                                                                <div>
                                                                    <span className="text-zinc-500">Invoice ID:</span>
                                                                    <span className="text-white ml-2">{brokerInvoice.invoice_id}</span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-zinc-500">Amount:</span>
                                                                    <span className="text-green-400 ml-2 font-semibold">
                                                                        ${(brokerInvoice.amount || selectedLoad?.gross_pay || 0).toFixed(2)}
                                                                    </span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-zinc-500">Due Date:</span>
                                                                    <span className="text-white ml-2">{brokerInvoice.due_date || 'N/A'}</span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-zinc-500">Created:</span>
                                                                    <span className="text-white ml-2">
                                                                        {brokerInvoice.created_at ? new Date(brokerInvoice.created_at).toLocaleDateString() : 'N/A'}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <div className="flex gap-2 pt-2">
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    className="border-zinc-700 text-zinc-300 hover:bg-zinc-700"
                                                                    onClick={() => generateBrokerInvoicePDF(brokerInvoice)}
                                                                    data-testid="download-broker-invoice-btn"
                                                                >
                                                                    <Printer className="w-4 h-4 mr-1" />
                                                                    Download PDF
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <p className="text-zinc-500 text-sm">No broker invoice generated yet</p>
                                                    )}
                                                </div>

                                                {/* Dispatcher (Commission) Invoice */}
                                                <div className="p-4 bg-zinc-900/50 rounded-lg border border-zinc-700" data-testid="dispatcher-invoice-section">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <div className="flex items-center gap-2">
                                                            <Receipt className="w-5 h-5 text-orange-400" />
                                                            <h4 className="text-white font-medium">Dispatcher Invoice (Commission)</h4>
                                                        </div>
                                                        {dispatcherInvoice && (
                                                            <Badge className={`${
                                                                dispatcherInvoice.status === 'paid' ? 'bg-green-500/20 text-green-400' :
                                                                dispatcherInvoice.status === 'invoiced' ? 'bg-amber-500/20 text-amber-400' :
                                                                'bg-zinc-500/20 text-zinc-400'
                                                            }`}>
                                                                {(dispatcherInvoice.status || 'unpaid').toUpperCase()}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    
                                                    {dispatcherInvoice ? (
                                                        <div className="space-y-2">
                                                            <div className="grid grid-cols-2 gap-2 text-sm">
                                                                <div>
                                                                    <span className="text-zinc-500">Invoice ID:</span>
                                                                    <span className="text-white ml-2">{dispatcherInvoice.invoice_id}</span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-zinc-500">Dispatch Fee:</span>
                                                                    <span className="text-orange-400 ml-2 font-semibold">
                                                                        ${dispatcherInvoice.dispatcher_fee?.toFixed(2)}
                                                                    </span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-zinc-500">Gross Rate:</span>
                                                                    <span className="text-white ml-2">${dispatcherInvoice.gross_rate?.toFixed(2)}</span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-zinc-500">Commission:</span>
                                                                    <span className="text-white ml-2">{dispatcherInvoice.commission_rate}%</span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-zinc-500">Invoice Date:</span>
                                                                    <span className="text-white ml-2">{dispatcherInvoice.invoice_date || 'N/A'}</span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-zinc-500">Due Date:</span>
                                                                    <span className="text-white ml-2">{dispatcherInvoice.due_date || 'N/A'}</span>
                                                                </div>
                                                            </div>
                                                            <div className="flex gap-2 pt-2">
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    className="border-zinc-700 text-zinc-300 hover:bg-zinc-700"
                                                                    onClick={() => generateDispatcherInvoicePDF(dispatcherInvoice)}
                                                                    data-testid="download-dispatcher-invoice-btn"
                                                                >
                                                                    <Printer className="w-4 h-4 mr-1" />
                                                                    Download PDF
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <p className="text-zinc-500 text-sm">No dispatcher invoice generated yet. Invoice is created when load is marked as delivered.</p>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </CardContent>
                                </Card>
                            </TabsContent>
                        </Tabs>
                    </div>

                    {/* Sidebar - Calculator & Actions */}
                    <div className="space-y-6">
                        {/* Audit Calculator */}
                        <Card className="bg-zinc-800/50 border-zinc-700">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base flex items-center gap-2">
                                    <Calculator className="w-4 h-4 text-orange-400" />
                                    Audit Calculator
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div className="flex justify-between items-center">
                                    <span className="text-zinc-400 text-sm">Gross Revenue</span>
                                    <span className="text-green-400 font-semibold">${financials.gross.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-zinc-400 text-sm">Commission ({financials.commissionRate}%)</span>
                                    <span className="text-amber-400">-${financials.commission.toFixed(2)}</span>
                                </div>
                                {financials.detention > 0 && (
                                    <div className="flex justify-between items-center">
                                        <span className="text-zinc-400 text-sm">Detention Pay</span>
                                        <span className="text-green-400">+${financials.detention.toFixed(2)}</span>
                                    </div>
                                )}
                                {financials.lumper > 0 && (
                                    <div className="flex justify-between items-center">
                                        <span className="text-zinc-400 text-sm">Lumper Fee</span>
                                        <span className="text-red-400">-${financials.lumper.toFixed(2)}</span>
                                    </div>
                                )}
                                <div className="border-t border-zinc-700 pt-3">
                                    <div className="flex justify-between items-center">
                                        <span className="text-zinc-400 text-sm">Tax Reserve (25%)</span>
                                        <span className="text-zinc-500">${financials.taxReserve.toFixed(2)}</span>
                                    </div>
                                </div>
                                <div className="border-t border-zinc-700 pt-3">
                                    <div className="flex justify-between items-center">
                                        <span className="text-white font-semibold">Net to Driver</span>
                                        <span className="text-white font-bold text-lg">${financials.netToDriver.toFixed(2)}</span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Quick Actions */}
                        <Card className="bg-zinc-800/50 border-zinc-700">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base text-white">Quick Actions</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                <Button
                                    variant="outline"
                                    className="w-full justify-start border-zinc-700 text-zinc-300 hover:bg-zinc-700"
                                    onClick={handleDownloadDisputeBundle}
                                    data-testid="dispute-bundle-btn"
                                >
                                    <Download className="w-4 h-4 mr-2 text-red-400" />
                                    Download Dispute Bundle
                                </Button>
                                <Button
                                    variant="outline"
                                    className="w-full justify-start border-zinc-700 text-zinc-300 hover:bg-zinc-700"
                                    onClick={() => navigate(`/loads/${selectedLoad.load_id}`)}
                                >
                                    <ChevronRight className="w-4 h-4 mr-2" />
                                    View Full Load Details
                                </Button>
                            </CardContent>
                        </Card>

                        {/* Truck Info */}
                        {truckProfile && (
                            <Card className="bg-zinc-800/50 border-zinc-700">
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base text-white">Assigned Truck</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-zinc-400 text-sm">Truck</span>
                                        <span className="text-white">{truckProfile.name || truckProfile.truck_id}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-zinc-400 text-sm">Commission Setting</span>
                                        <span className="text-orange-400 font-semibold">{truckProfile.commission_percent}%</span>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </div>
            )}

            {/* Unlink Dialog */}
            <Dialog open={unlinkDialog.open} onOpenChange={(open) => !open && setUnlinkDialog({ open: false, document: null })}>
                <DialogContent className="bg-zinc-900 border-zinc-800">
                    <DialogHeader>
                        <DialogTitle className="text-white">Unlink Document</DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            This will remove the document from this load but keep it in the system.
                        </DialogDescription>
                    </DialogHeader>
                    {unlinkDialog.document && (
                        <div className="py-4">
                            <p className="text-white">{unlinkDialog.document.filename || unlinkDialog.document.document_type}</p>
                            <p className="text-zinc-500 text-sm">ID: {unlinkDialog.document.document_id}</p>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setUnlinkDialog({ open: false, document: null })} className="border-zinc-700">
                            Cancel
                        </Button>
                        <Button onClick={handleUnlinkDocument} className="bg-amber-600 hover:bg-amber-700">
                            Unlink Document
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Replace Dialog */}
            <Dialog open={replaceDialog.open} onOpenChange={(open) => !open && setReplaceDialog({ open: false, document: null })}>
                <DialogContent className="bg-zinc-900 border-zinc-800">
                    <DialogHeader>
                        <DialogTitle className="text-white">Replace Document</DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            Upload a new file to replace the existing document.
                        </DialogDescription>
                    </DialogHeader>
                    {replaceDialog.document && (
                        <div className="py-4 space-y-4">
                            <div>
                                <p className="text-zinc-400 text-sm">Replacing:</p>
                                <p className="text-white">{replaceDialog.document.filename || replaceDialog.document.document_type}</p>
                            </div>
                            <div>
                                <Input
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={(e) => setReplaceFile(e.target.files[0])}
                                    className="bg-zinc-800 border-zinc-700 text-white"
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setReplaceDialog({ open: false, document: null })} className="border-zinc-700">
                            Cancel
                        </Button>
                        <Button onClick={handleReplaceDocument} disabled={!replaceFile} className="bg-blue-600 hover:bg-blue-700">
                            Replace Document
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
